import React, { useEffect, useMemo, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleMinus,
  faEllipsis,
  faPenToSquare,
  faPlus,
} from "@fortawesome/pro-regular-svg-icons";
import TdLink from "../../components/TdLink";
import { getAllCustomerTokens, deleteToken } from "../../api/Tokens";
import { useAuth, Roles } from "../../providers/AuthContext";
import CustomDropdown from "../../components/CustomDropdown";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import DataTable from "../../components/DataTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import { useTranslation } from "react-i18next";
import { getDecryptedTokenDisplay, getTokenValidLabel } from "../../helpers/TokenValidity";

const TokensTable = ({ customerPublicId }) => {
  const { t } = useTranslation();
  const { getAccessToken, hasRole } = useAuth();
  const navigate = useNavigate();
  const [tokens, setTokens] = useState([]);
  const [search, setSearch] = useState("");
  const [tokensLoaded, setTokensLoaded] = useState(true);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });

  useEffect(() => {
    if (customerPublicId) {
      handleGetTokens();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, [customerPublicId]);

  const handleGetTokens = async () => {
    setTokensLoaded(true);

    try {
      const accessToken = await getAccessToken();
      const newTokens = await getAllCustomerTokens(customerPublicId, accessToken);
      setTokens(newTokens);
    } catch (error) {
      console.error(error);
    } finally {
      setTokensLoaded(false);
    }
  };

  const handleDeleteToken = async (idTagPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.token") }));

    try {
      const accessToken = await getAccessToken();
      await deleteToken(idTagPublicId, accessToken);
      const newTokens = await getAllCustomerTokens(customerPublicId, accessToken);
      setTokens(newTokens);
      toast.success(t("toast.deleted", { entity: t("entity.token") }), { id: toastId });
    } catch (error) {
      toast.error(t("toast.failedDelete", { entity: t("entity.token") }), { id: toastId });
      console.error(error);
    }
  };

  const columns = useMemo(
    () => [
      {
        key: "name",
        header: t("common.name"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => token.name || token.visualNumber || "",
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {token.name || token.visualNumber}
          </TdLink>
        ),
      },
      {
        key: "issuer",
        header: t("table.issuer"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => token.issuer || "Gito",
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {token.issuer || "Gito"}
          </TdLink>
        ),
      },
      {
        key: "tagToken",
        header: t("entity.token"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => token.tagToken || "",
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {token.tagToken || ""}
          </TdLink>
        ),
      },
      {
        key: "decryptedTagToken",
        header: t("token.decrypted"),
        sortable: true,
        rawCell: true,
        hidden: !hasRole(Roles.Admin) && !hasRole(Roles.CpmsLight),
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => getDecryptedTokenDisplay(token, t),
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {getDecryptedTokenDisplay(token, t)}
          </TdLink>
        ),
      },
      {
        key: "tokenType",
        header: t("common.type"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => token.tokenType || "",
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {token.tokenType || ""}
          </TdLink>
        ),
      },
      {
        key: "valid",
        header: t("common.valid"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => getTokenValidLabel(token, t),
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {getTokenValidLabel(token, t)}
          </TdLink>
        ),
      },
      {
        key: "isFromOcpi",
        header: t("table.externalToken"),
        sortable: true,
        rawCell: true,
        disabled: (token) => token.lastUpdatedBy === "OCPI: Created via PUT /token",
        getSortValue: (token) => (token.isFromOcpi ? "yes" : "no"),
        render: (token) => (
          <TdLink to={`/token/${token.idTagPublicId}`}>
            {token.isFromOcpi ? t("common.yes") : t("common.no")}
          </TdLink>
        ),
      },
      {
        key: "actions",
        header: (
          <div className="flex w-full justify-end items-center">
            <button
              className="block text-sm text-primary-600 hover:text-primary-400 text-left rounded-lg"
              onClick={() => {
                navigate("/token/create", { state: { customerPublicId } });
              }}
            >
              <FontAwesomeIcon icon={faPlus} /> {t("common.addNew")}
            </button>
          </div>
        ),
        headerClassName: "text-left",
        sortable: false,
        searchable: false,
        rawCell: true,
        render: (token) => {
          const isOcpiCreated = token.lastUpdatedBy === "OCPI: Created via PUT /token";

          return (
            <td className="flex justify-end">
              {token.isFromOcpi === false && (
                <CustomDropdown
                  isRight={true}
                  ContentComponent={() => (
                    <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                      {!isOcpiCreated ? (
                        <NavLink
                          className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                          to={`/token/${token.idTagPublicId}`}
                        >
                          <FontAwesomeIcon icon={faPenToSquare} /> {t("common.edit")}
                        </NavLink>
                      ) : (
                        ""
                      )}

                      <button
                        className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                        onClick={() => {
                          setConfirmState({
                            isOpen: true,
                            title: t("confirm.deleteEntity", {
                              entity: t("entity.token"),
                              name: token.name || token.visualNumber,
                            }),
                            confirm: () => {
                              handleDeleteToken(token.idTagPublicId);
                            },
                          });
                        }}
                      >
                        <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                      </button>
                    </div>
                  )}
                >
                  <div className="w-8 h-6 flex justify-end bg-red items-center cursor-tokener">
                    <FontAwesomeIcon icon={faEllipsis} />
                  </div>
                </CustomDropdown>
              )}
            </td>
          );
        },
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
    [customerPublicId, navigate, t, hasRole]
  );

  return (
    <>
      <div className="page-content">
        <div className="flex flex-col w-full ">
          {tokensLoaded ? (
            <div className="rounded-md shadow-md w-full p-8">
              <LoadingSpinner text={t("common.loadingEntity", { name: t("entity.tokens") })} />
            </div>
          ) : (
            <DataTable
              rows={tokens}
              columns={columns}
              search={search}
              onSearchChange={setSearch}
              searchPlaceholder={t("search.token")}
              rowKey={(token, index) => token.idTagPublicId || index}
              emptyColSpan={hasRole(Roles.Admin) || hasRole(Roles.CpmsLight) ? 8 : 7}
              defaultRowsPerPage={30}
            />
          )}

          <h1 className="mt-4 text-lg font-semibold">{t("common.totalTokens", { count: tokens.length })}</h1>
        </div>

        <ConfirmationModal
          isOpen={confirmState.isOpen}
          title={confirmState.title}
          onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
          onConfirm={() => {
            setConfirmState((s) => ({ ...s, isOpen: false }));
            confirmState.confirm();
          }}
        />
      </div>
    </>
  );
};

export default TokensTable;
